import React, { useState } from "react";
import { fileUrl, api } from "@/lib/api";
import { Trash2, Upload, GripVertical, Star } from "lucide-react";
import { errorDetail } from "@/lib/utils";

/**
 * Drag-to-reorder image gallery for the admin edit page.
 * Uses native HTML5 drag-and-drop. The first image is highlighted as the "Hero" image.
 */
export default function SortableImageGrid({
  productId,
  images,
  onImagesChange,
  onAddFiles,
}) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const remove = async (fileId) => {
    if (!window.confirm("Remove this image from the product?")) return;
    try {
      await api.delete(`/products/${productId}/images/${fileId}`);
      onImagesChange(images.filter((i) => i.id !== fileId));
    } catch (e) {
      alert(errorDetail(e) || "Failed to remove image");
    }
  };

  const persistOrder = async (next) => {
    onImagesChange(next);
    try {
      await api.patch(`/products/${productId}/images/order`, {
        ordered_ids: next.map((i) => i.id),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    setOverIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(null);
    setOverIdx(null);
    persistOrder(next);
  };
  const onDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };
  const makeHero = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    persistOrder(next);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {images.map((img, idx) => (
          <div
            key={img.id}
            draggable
            onDragStart={onDragStart(idx)}
            onDragOver={onDragOver(idx)}
            onDrop={onDrop(idx)}
            onDragEnd={onDragEnd}
            data-testid={`sortable-image-${idx}`}
            className={`relative group cursor-move transition-all ${
              dragIdx === idx ? "opacity-40" : ""
            } ${overIdx === idx && dragIdx !== idx ? "rounded-xl ring-2 ring-heda-gold" : ""}`}
          >
            <img
              src={fileUrl(img.storage_path)}
              alt=""
              className="pointer-events-none h-24 w-full rounded-xl border border-heda-line object-cover"
            />

            {/* Hero badge */}
            {idx === 0 && (
              <div className="absolute left-1 top-1 flex items-center gap-1 rounded-full bg-heda-gold px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                <Star size={9} strokeWidth={2.5} fill="white" /> Hero
              </div>
            )}

            {/* Drag grip indicator */}
            <div className="absolute right-1 top-1 rounded-full border border-heda-line bg-white/95 p-1 opacity-0 transition-opacity group-hover:opacity-100">
              <GripVertical size={11} className="text-heda-mute" />
            </div>

            {/* Action buttons (bottom row) */}
            <div className="absolute inset-x-1 bottom-1 flex items-center justify-between gap-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
              {idx !== 0 && (
                <button
                  type="button"
                  onClick={() => makeHero(idx)}
                  title="Set as hero image"
                  data-testid={`set-hero-${idx}`}
                  className="rounded-full border border-heda-line bg-white/95 p-1 text-heda-ink hover:bg-heda-gold hover:text-white"
                >
                  <Star size={11} />
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(img.id)}
                title="Remove image"
                data-testid={`product-image-remove-${idx}`}
                className="ml-auto rounded-full border border-heda-line bg-white/95 p-1 text-heda-ink hover:bg-red-700 hover:text-white"
              >
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
        <label className="flex h-24 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-heda-line bg-heda-cream/40 transition hover:border-heda-gold">
          <input
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={(e) => onAddFiles(e.target.files)}
            data-testid="add-image-input"
          />
          <Upload size={16} className="text-heda-mute" />
        </label>
      </div>
      <p className="mt-2.5 text-[11.5px] leading-relaxed text-heda-mute">
        Drag images to reorder · the first image (gold star) is the hero shown after a QR scan.
      </p>
    </div>
  );
}
